import { Map as ImmutableMap } from 'immutable';
import { v4 as uuid } from 'uuid';

import { goalsSelectors } from 'app-components/flux/goals';
import { createDialog } from 'app-components/flux/root-ui/rootUiActions';
import { mkRootUiData } from 'app-components/flux/root-ui/rootUiTypes';
import { getLogger } from 'app-components/utils/core';
import tracker from 'app-components/utils/core/tracker';
import store from 'app-components/utils/redux-store';
import type RootState from 'app-components/utils/redux-store/rootState';

import { safeParse } from '../../aiChatUtils';
import { returnToolResponse } from '../llmToolsFramework';
import type { ToolMessage } from '../llmToolsTypes';

const log = getLogger('llmGoalsTools');

const VALID_GOAL_TYPES = [
  'SAVE_FOR_EMERGENCY_FUND',
  'SAVE_FOR_VACATION',
  'SAVE_FOR_WEDDING',
  'SAVE_FOR_CAR',
  'SAVE_FOR_HOME',
  'SAVE_FOR_OTHER',
];

const findGoalsByNameOrType = (state: RootState, name?: string, type?: string) => {
  const goalsById = goalsSelectors.getResourcesById(state);

  if (!goalsById || goalsById.size === 0) {
    return [];
  }

  const nameLower = name?.toLowerCase();

  return goalsById.valueSeq().reduce((acc: any[], goal: any) => {
    if (goal.isDeleted) return acc;
    if (nameLower && !goal.name?.toLowerCase().includes(nameLower)) return acc;
    if (type && goal.type !== type) return acc;

    acc.push({
      id: goal.id,
      type: goal.type,
      name: goal.name,
      targetAmount: goal.targetAmount,
      targetOn: goal.targetOn,
      savedSoFar: goal.savedSoFar,
    });
    return acc;
  }, []);
};

export const goalsTools = {
  search_goals: {
    definition: {
      name: 'search_goals',
      description: `Search for goals by name and/or type. Use this to find a specific goal before updating it. Returns matching goals with their IDs. If you need to update a goal, first search for it using this tool, then present the matches to the user to confirm which one to update. NEVER show goal IDs to the user - always display goal names only. Be concise and brief in your response. Available goal operations: create, update, contribute. You cannot archive or delete goals.`,
      parameters: {
        type: 'object' as const,
        properties: {
          name: {
            type: 'string' as const,
            description: 'Search by goal name (partial match, case-insensitive)',
          },
          type: {
            type: 'string' as const,
            description: 'Filter by goal type',
            enum: VALID_GOAL_TYPES,
          },
        },
        required: [],
      },
    },
    handler: (msg: ToolMessage) => {
      if (!store) {
        log.error('llmTools/goals: Store is not available');
        return returnToolResponse(msg, { goals: [], error: 'Store not available' });
      }

      const args = safeParse(msg.arguments) || {};
      const { name, type } = args;

      if (!name && !type) {
        return returnToolResponse(msg, {
          success: false,
          error: 'Please provide at least a name or type to search for.',
        });
      }

      const state = store.getState() as RootState;
      const matches = findGoalsByNameOrType(state, name, type);

      log.debug('llmTools/goals: Search found goals:', matches.length);

      if (matches.length === 0) {
        return returnToolResponse(msg, {
          goals: [],
          message: `No goals found matching ${name ? `name "${name}"` : ''}${name && type ? ' and ' : ''}${type ? `type "${type}"` : ''}.`,
        });
      }

      return returnToolResponse(msg, {
        goals: matches,
        message:
          matches.length === 1
            ? `Found 1 goal matching your search.`
            : `Found ${matches.length} goals matching your search. Please ask the user which one they want to update.`,
      });
    },
  },

  list_goals: {
    definition: {
      name: 'list_goals',
      description: `Retrieves all goals (savings goals) for the user. Returns a list of goals including their type, name, target amount, target date, current saved amount, and other details. Use this to show the user their goals or to find a specific goal. NEVER show goal IDs to the user - always display goal names only. Be concise and brief in your response. Available goal operations: create, update, contribute. You cannot archive or delete goals.`,
      parameters: {
        type: 'object' as const,
        properties: {},
        required: [],
      },
    },
    handler: (msg: ToolMessage) => {
      if (!store) {
        log.error('llmTools/goals: Store is not available');
        return returnToolResponse(msg, { goals: [], error: 'Store not available' });
      }

      const state = store.getState() as RootState;
      const goalsById = goalsSelectors.getResourcesById(state);

      log.debug('llmTools/goals: Fetching all goals');

      if (!goalsById || goalsById.size === 0) {
        return returnToolResponse(msg, { goals: [], message: 'No goals found.' });
      }

      const goalsList = goalsById
        .valueSeq()
        .map((goal: any) => ({
          id: goal.id,
          type: goal.type,
          name: goal.name,
          targetAmount: goal.targetAmount,
          targetOn: goal.targetOn,
          startOn: goal.startOn,
          savedSoFar: goal.savedSoFar,
          spent: goal.spent,
          contributionAmount: goal.contributionAmount,
          contributionFrequency: goal.contributionFrequency,
          description: goal.description,
          notes: goal.notes,
          completedOn: goal.completedOn,
          isDeleted: goal.isDeleted,
        }))
        .toArray();

      log.debug('llmTools/goals: Retrieved goals:', goalsList.length);
      return returnToolResponse(msg, { goals: goalsList });
    },
  },

  upsert_goal: {
    definition: {
      name: 'upsert_goal',
      description: `Creates a new savings goal or updates an existing one. Opens a dialog for user to review and confirm.
FOR UPDATES: First use search_goals to find the goal by name, present matches to user for confirmation, then call this tool with the id from search results.
FOR CREATES: Ask clarifying questions for ALL missing required fields before creating. Required: name, type, targetAmount, targetOn. Do NOT call this tool until user confirms all required fields.
Optional fields: startOn (defaults to today), contributionAmount, contributionFrequency, notes, description, isTakenFromF2S.
Valid types: SAVE_FOR_EMERGENCY_FUND, SAVE_FOR_VACATION, SAVE_FOR_WEDDING, SAVE_FOR_CAR, SAVE_FOR_HOME, SAVE_FOR_OTHER. Be concise and brief in your response.`,
      parameters: {
        type: 'object' as const,
        properties: {
          id: {
            type: 'string' as const,
            description: 'The goal ID (required for updating an existing goal, omit for creating a new goal)',
          },
          name: {
            type: 'string' as const,
            description: 'The name of the goal',
          },
          type: {
            type: 'string' as const,
            description: 'The type of goal',
            enum: VALID_GOAL_TYPES,
          },
          targetAmount: {
            type: 'number' as const,
            description: 'The target amount to save for this goal (in dollars)',
          },
          targetOn: {
            type: 'string' as const,
            description:
              'The target date to reach the goal (ISO 8601 format: YYYY-MM-DD). Pass null or empty string to clear the target date.',
          },
          startOn: {
            type: 'string' as const,
            description: 'The start date of the goal (ISO 8601 format: YYYY-MM-DD). Defaults to today if not provided.',
          },
          contributionAmount: {
            type: 'number' as const,
            description: 'The amount to contribute per period (optional)',
          },
          contributionFrequency: {
            type: 'string' as const,
            description: 'How often to contribute (MONTHLY, WEEKLY, BIWEEKLY)',
          },
          notes: {
            type: 'string' as const,
            description: 'Additional notes about the goal (optional)',
          },
          description: {
            type: 'string' as const,
            description: 'A description of what this goal is for (optional)',
          },
          isTakenFromF2S: {
            type: 'boolean' as const,
            description:
              'Whether to include this goal in the spending plan (Free To Spend). When true, goal contributions are deducted from available spending money. Defaults to false.',
          },
        },
        required: [],
      },
    },
    handler: (msg: ToolMessage) => {
      tracker.track(tracker.events.AIChatGoalCreate);

      if (!store) {
        log.error('llmTools/goals: Store is not available');
        return returnToolResponse(msg, { success: false, error: 'Store not available' });
      }

      const args = safeParse(msg.arguments) || {};
      const state = store.getState() as RootState;

      const {
        id,
        name,
        type,
        targetAmount,
        targetOn,
        startOn,
        contributionAmount,
        contributionFrequency,
        notes,
        description,
        isTakenFromF2S,
      } = args;

      const isUpdate = !!id;

      log.debug(`llmTools/goals: ${isUpdate ? 'Updating' : 'Creating'} goal with args:`, args);

      if (isUpdate) {
        const goalsById = goalsSelectors.getResourcesById(state);
        const existingGoal = goalsById.get(id);

        if (!existingGoal) {
          const errorMsg = `Goal not found with ID: ${id}`;
          log.error('llmTools/goals:', errorMsg);
          return returnToolResponse(msg, { success: false, error: errorMsg });
        }

        if (type && !VALID_GOAL_TYPES.includes(type)) {
          const errorMsg = `Invalid goal type "${type}". Valid types are: ${VALID_GOAL_TYPES.join(', ')}`;
          log.error('llmTools/goals:', errorMsg);
          return returnToolResponse(msg, { success: false, error: errorMsg });
        }
      } else {
        if (!name || !type || !targetAmount || !targetOn) {
          const errorMsg =
            'Missing required fields for creating a goal. name, type, targetAmount, and targetOn are required.';
          log.error('llmTools/goals:', errorMsg);
          return returnToolResponse(msg, { success: false, error: errorMsg });
        }

        if (!VALID_GOAL_TYPES.includes(type)) {
          const errorMsg = `Invalid goal type "${type}". Valid types are: ${VALID_GOAL_TYPES.join(', ')}`;
          log.error('llmTools/goals:', errorMsg);
          return returnToolResponse(msg, { success: false, error: errorMsg });
        }
      }

      const operation = isUpdate ? 'update' : 'create';

      return new Promise((resolve) => {
        const callback = (result: { success: boolean; goalId?: string; error?: string }) => {
          if (result.success) {
            log.debug('upsert_goal: Success', { operation });
            resolve(returnToolResponse(msg, { success: true, message: `Goal ${operation}d successfully.` }));
          } else {
            log.error('upsert_goal: Failed', { error: result.error });
            resolve(returnToolResponse(msg, { success: false, error: result.error || `Failed to ${operation} goal` }));
          }
        };

        const data = {
          id,
          name,
          type,
          targetAmount,
          targetOn,
          startOn,
          contributionAmount,
          contributionFrequency,
          notes,
          description,
          isTakenFromF2S,
        };

        const dialogData = mkRootUiData({
          type: 'LLM_GOAL',
          id: `llm-goal-${operation}-${uuid()}`,
          visible: true,
          allowNesting: false,
          props: ImmutableMap({ operation, data, callback }),
        });

        log.debug('upsert_goal: Dispatching dialog');
        store?.dispatch(createDialog(dialogData));
      });
    },
  },

  contribute_to_goal: {
    definition: {
      name: 'contribute_to_goal',
      description: `Add funds to an existing goal. Opens a dialog for the user to review and confirm the contribution.

Use search_goals or list_goals first to find the goal ID.

- goalId: ID of the goal to contribute to (required)
- amount: Amount to contribute (in dollars)

Be concise and brief in your response.`,
      parameters: {
        type: 'object' as const,
        properties: {
          goalId: {
            type: 'string' as const,
            description: 'The ID of the goal to contribute to (from search_goals or list_goals)',
          },
          amount: {
            type: 'number' as const,
            description: 'The amount to contribute (in dollars)',
          },
        },
        required: ['goalId'],
      },
    },
    handler: (msg: ToolMessage) => {
      const args = safeParse(msg.arguments) || {};
      const { goalId, amount } = args;

      if (!goalId) {
        return returnToolResponse(msg, { success: false, error: 'goalId is required' });
      }

      const state = store?.getState() as RootState;
      const goal = goalsSelectors.getGoalById(state, goalId);

      if (!goal) {
        return returnToolResponse(msg, { success: false, error: `Goal not found with ID: ${goalId}` });
      }

      log.debug('contribute_to_goal:', { goalId, goalName: goal.name, amount });

      return new Promise((resolve) => {
        const callback = (result: { success: boolean; error?: string }) => {
          if (result.success) {
            log.debug('contribute_to_goal: Success', { goalId });
            resolve(returnToolResponse(msg, { success: true, message: `Contribution to "${goal.name}" successful.` }));
          } else {
            log.error('contribute_to_goal: Failed', { error: result.error });
            resolve(returnToolResponse(msg, { success: false, error: result.error || 'Failed to contribute to goal' }));
          }
        };

        const data = { goalId, amount };

        const dialogData = mkRootUiData({
          type: 'LLM_GOAL_CONTRIBUTE',
          id: `llm-goal-contribute-${uuid()}`,
          visible: true,
          allowNesting: false,
          props: ImmutableMap({ data, callback }),
        });

        log.debug('contribute_to_goal: Dispatching dialog');
        store?.dispatch(createDialog(dialogData));
      });
    },
  },
};
